import React, { useCallback, useMemo, useState } from 'react';
import {
  selectErroreInserimento,
  selectErroriCarrelloSport,
  selectHasSospesi,
  selectIsScommettibile,
  selectIsSistema,
  selectTicket,
} from '../../sportTicketSelectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useScommettiSingolaMultiplaSportMutation, useScommettiSistemaSportMutation } from 'features/api/sportApiSlice';

import { Button } from 'components/button';
import ConfirmationModal from 'features/sport/components/sportTicket/components/confirmationModal/ConfirmationModal';
import { PaymentMethodEnum } from 'features/carrello/carrelloSlice';
import { SportTicket } from 'features/sport/components/sportTicket/utils/calcoloCarrello/types';
import classNames from 'classnames';
import { selectPaymentType } from 'features/carrello/carrelloSelectors';
import { setIsOpenLoginModalValue } from 'features/modal/modalSlice';
import styles from './SportTicketActionScommetti.module.scss';
import { useBlockScroll } from 'hooks/useBlockScroll';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks';
import { useIsSaldoGiocabile } from 'features/carrello/hooks';

export type SportTicketActionScommettiProps = {
  btnClassName?: string;
};

export const SportTicketActionScommetti = ({ btnClassName }: SportTicketActionScommettiProps) => {
  const dispatch = useAppDispatch();

  const { isAuthenticated, updateSession } = useInternalSession();
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);

  const [scommettiButtonLabel] = useGetLabelsByKeys(['vendita-action-scommetti-button'])!;

  const sportTicket = useTypedSelector(selectTicket);
  const errori = useTypedSelector(selectErroriCarrelloSport);
  const isSistema = useTypedSelector(selectIsSistema);
  const hasSospesi = useTypedSelector(selectHasSospesi);
  const isScommettibile = useTypedSelector(selectIsScommettibile);
  const paymentMethodSelected = useTypedSelector(selectPaymentType);
  const erroreInserimento = useTypedSelector(selectErroreInserimento);

  const [scommettiSingolaMultiplaSport, { isLoading: isLoadingSingolaMultipla }] =
    useScommettiSingolaMultiplaSportMutation();
  const [scommettiSistemaSport, { isLoading: isLoadingSistema }] = useScommettiSistemaSportMutation();

  const isSaldoGiocabile = useIsSaldoGiocabile();
  useBlockScroll(isModalOpened);

  const isBonusGoldSelected = useMemo(() => paymentMethodSelected === PaymentMethodEnum.Gold, [paymentMethodSelected]);

  const canBet = useMemo(
    () =>
      [isScommettibile, errori?.length === 0, erroreInserimento === undefined, isSaldoGiocabile, !hasSospesi].every(
        Boolean
      ) &&
      (!isLoadingSingolaMultipla || !isLoadingSistema),
    [
      isScommettibile,
      errori?.length,
      erroreInserimento,
      isSaldoGiocabile,
      hasSospesi,
      isLoadingSingolaMultipla,
      isLoadingSistema,
    ]
  );
  const disableBtn = useMemo(() => isAuthenticated && !canBet, [isAuthenticated, canBet]);

  const onScommettiSport = useCallback(
    async (ticket: SportTicket) => {
      if (!isAuthenticated) {
        dispatch(setIsOpenLoginModalValue(true));
        return;
      }
      if (canBet && ticket) {
        setIsModalOpened(false);
      }
      const actionToBet = isSistema ? scommettiSistemaSport : scommettiSingolaMultiplaSport;
      await actionToBet(ticket);
      if (isBonusGoldSelected) {
        updateSession({ user: { flg_bw: '1' } });
      }
    },
    [
      isAuthenticated,
      canBet,
      isSistema,
      scommettiSistemaSport,
      scommettiSingolaMultiplaSport,
      dispatch,
      isBonusGoldSelected,
      updateSession,
    ]
  );

  return (
    <>
      <ConfirmationModal
        onConfirm={(updatedTicket) => onScommettiSport(updatedTicket)}
        onCancel={() => setIsModalOpened(false)}
        isOpened={isModalOpened}
      />
      <Button
        type="button"
        size="medium"
        disabled={disableBtn}
        className={classNames(styles.button, btnClassName, { [styles.disable]: disableBtn })}
        visualizationType={'primary'}
        onClick={() => (isBonusGoldSelected ? setIsModalOpened(true) : onScommettiSport(sportTicket))}
      >
        {scommettiButtonLabel}
      </Button>
    </>
  );
};